import { graphql, SliceComponentProps } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';

import { useUser } from '../../context/UserContext';
import { Serialize } from '../../helpers/api/interfaces/serialize.interface';
import {
  authenticateWithCode,
  getProfileDetails,
  logOutUser,
} from '../../helpers/api/user-profile-api';
import { isBrowser } from '../../helpers/is-browser';
import {
  checkIfValidUser,
  clearUrlParams,
  getLocalStorageItem,
} from '../../helpers/utils';
import useGetMobileAppWebViewHeader from '../../hooks/use-get-mobile-app-web-view';
import { ILibraryMeetingPlace } from '../../queries/fragments/libraryMeetingPlace';
import { ILibraryMenuGroup } from '../../queries/fragments/libraryMenuGroup';
import { IMeetingPlacePageSearchResults } from '../../queries/fragments/meetingPlacePageSearchResults';
import {
  IImageWrapperFile,
  IImageWrapperSvg,
} from '../../queries/fragments/svg';
import { PageContext } from '../../templates/page-context.interface';
import { ITextAndImageCardBlock } from '../blocks/text-and-image-card-block/text-and-image-card-block';
import { Header } from '../header';
import SmartAppBanner from '../smart-app-banner/smart-app-banner';
import { TabBar } from '../tab-bar';
import Spinner from '../ui/spinner/spinner';
import { Whatsapp } from '../whatsapp-messenger/whatsapp';

type LayoutProps = {
  contentfulMeetingPlacePageHome: {
    whatsappNumber: number;
    hideWhatsapp?: boolean;
    meetingPlace?: ILibraryMeetingPlace;
    menuItem: Array<ILibraryMenuGroup>;
    menuCards: ITextAndImageCardBlock[];
    searchResults?: IMeetingPlacePageSearchResults;
  };
  playStoreImage?: {
    gatsbyImageData: IGatsbyImageData;
    description: string;
    svg: IImageWrapperSvg;
    file: IImageWrapperFile;
  };
  appStoreImage?: {
    gatsbyImageData: IGatsbyImageData;
    description: string;
    svg: IImageWrapperSvg;
    file: IImageWrapperFile;
  };
};

type AdditionalProps = { pageContext: Serialize<PageContext> };

const loginEnabled = process.env.GATSBY_ICIM_LOGIN_ENABLED === 'true';

export const Layout = ({
  children,
  data,
  pageContext,
}: SliceComponentProps<LayoutProps, '', AdditionalProps>) => {
  const meetingPlace = data?.contentfulMeetingPlacePageHome?.meetingPlace;
  const menuCards = data?.contentfulMeetingPlacePageHome?.menuCards;
  const menuItem = data?.contentfulMeetingPlacePageHome?.menuItem;
  const searchResults = data?.contentfulMeetingPlacePageHome?.searchResults;
  const whatsappNumber = data.contentfulMeetingPlacePageHome?.whatsappNumber;
  const hideWhatsapp = pageContext.hideWhatsapp;
  const playStoreImage = data?.playStoreImage;
  const appStoreImage = data?.appStoreImage;

  const isWebView = useGetMobileAppWebViewHeader();
  const { setUser } = useUser();
  const [showSpinner, setShowSpinner] = useState<boolean>(false);

  const sendEventToDataLayer = () => {
    if (isBrowser && dataLayer) {
      dataLayer.push({
        event: 'user_signIn',
      });
    }
  };

  useEffect(() => {
    // LW-1743: Send event to GTM when user clicks on a link to IKEA
    const sendEventToGtm = (e: Event) => {
      const target = e.target as HTMLLinkElement;

      if (!isBrowser || !target) return;

      let href: string | null = null;

      if (target instanceof HTMLAnchorElement) {
        href = target.href;
      } else {
        const closestAnchor = target.closest(
          '.ikea',
        ) as HTMLAnchorElement | null;
        if (closestAnchor) href = closestAnchor.href;
      }

      if (href) {
        const matches = href.match(/(http|https):\/\/(www\.|)ikea\.(.*)/);
        if (matches && dataLayer) {
          dataLayer.push({
            event: 'click_to_ikea',
          });
        }
      }
    };
    const element = window.document.body;
    element.addEventListener('click', sendEventToGtm);
    return () => {
      element.removeEventListener('click', sendEventToGtm);
    };
  }, []);

  useEffect(() => {
    if (isBrowser) {
      const windowUrl = window.location.search;
      const params = new URLSearchParams(windowUrl);
      const newsletter = params.get('newsletter');
      const authCode = params.get('code');
      // check for newsletter param to open newsletter side modal
      if (newsletter === 'true') {
        const newsletterButton = document.getElementById('newsletter-button');
        newsletterButton?.click();
      }
      // Logout user if the status is deleted (Profile delete)
      if (params.get('status') === 'deleted') {
        setUser(null);
        logOutUser();
        clearUrlParams();
      }
      // Authenticate and user login
      if (loginEnabled && authCode) {
        const currentPath = getLocalStorageItem('current_path');
        setShowSpinner(true);
        authenticateWithCode(authCode, meetingPlace?.businessUnitCode || '')
          .then((res) => {
            const isValidUser = checkIfValidUser(res);
            if (isValidUser) {
              setUser(res);
              if (currentPath) {
                window.location.href = currentPath;
              }
              sendEventToDataLayer();
            }
          })
          .finally(() => {
            setShowSpinner(false);
            localStorage.removeItem('current_path');
            clearUrlParams();
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrowser && window.location.search]);

  useEffect(() => {
    if (isBrowser && loginEnabled) {
      const token = Cookies.get('user_token');
      if (token) {
        getProfileDetails(token, meetingPlace?.businessUnitCode || '').then(
          (user) => {
            const isValidUser = checkIfValidUser(user);
            if (isValidUser) {
              setUser(user);
            }
          },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {whatsappNumber && !hideWhatsapp && !isWebView && (
        <Whatsapp whatsappNumber={whatsappNumber} />
      )}
      {meetingPlace && !isWebView && (
        <TabBar
          menuGroups={menuItem}
          menuCards={menuCards}
          logo={meetingPlace?.logo}
          socialMediaLinks={meetingPlace?.socialMediaLinks}
          meetingPlace={meetingPlace}
          node_locale={meetingPlace?.node_locale}
          searchResults={searchResults}
          pageContext={pageContext}
        />
      )}
      {meetingPlace &&
        (process.env.GATSBY_IOS_APP_ID || process.env.GASTBY_ANDROID_APP_ID) &&
        !isWebView && (
          <SmartAppBanner
            title={meetingPlace?.name}
            appStoreImage={appStoreImage}
            playStoreImage={playStoreImage}
            node_locale={meetingPlace.node_locale}
          />
        )}
      <div
        className={`smartbanner-top-margin relative xl:container lg:px-28 xl:px-28 ${
          isWebView ? '!pb-6' : ''
        }`}
      >
        {meetingPlace && !isWebView && (
          <Header meetingPlace={meetingPlace} logo={meetingPlace?.logo} />
        )}
        {children}
      </div>
      <Spinner show={showSpinner} />
    </>
  );
};

export default Layout;

export const query = graphql`
  query LayoutSliceQuery(
    $tagId: String!
    $node_locale: String!
    $appStoreImageId: String
    $playStoreImageId: String
  ) {
    contentfulMeetingPlacePageHome(
      metadata: { tags: { elemMatch: { contentful_id: { eq: $tagId } } } }
      node_locale: { eq: $node_locale }
    ) {
      menuItem {
        ...LibraryMenuGroup
      }
      menuCards {
        ...TextAndImageCardBlock
      }
      whatsappNumber
      meetingPlace {
        ... on Node {
          ... on ContentfulLibraryMeetingPlace {
            name
            node_locale
            businessUnitCode
            logo {
              description
              gatsbyImageData(width: 144, formats: AVIF)
              ... on ContentfulAsset {
                ...Svg
              }
            }
            openingHours {
              ... on ContentfulLibraryOpeningHours {
                ...LibraryOpeningHours
              }
            }
            openingHoursExceptions {
              ... on ContentfulLibraryOpeningHoursException {
                ...LibraryOpeningHoursExceptions
              }
            }
            openingHoursLink {
              ...MeetingPlaceLinkToAnyPage
            }
            timezone
            socialMediaLinks {
              ...LibrarySocialMediaLinks
            }
            primaryLanguage {
              ... on ContentfulLibraryLanguage {
                ...LibraryLanguage
              }
            }
            languages {
              ... on ContentfulLibraryLanguage {
                ...LibraryLanguage
              }
            }
            showLanguageSelector
          }
        }
      }
      searchResults {
        ...MeetingPlacePageSearchResults
      }
    }

    appStoreImage: contentfulAsset(
      contentful_id: { eq: $appStoreImageId }
      node_locale: { eq: $node_locale }
    ) {
      gatsbyImageData(width: 140, height: 60, formats: AVIF)
      description
      ... on ContentfulAsset {
        ...Svg
      }
    }
    playStoreImage: contentfulAsset(
      contentful_id: { eq: $playStoreImageId }
      node_locale: { eq: $node_locale }
    ) {
      gatsbyImageData(width: 140, height: 60, formats: AVIF)
      description
      ... on ContentfulAsset {
        ...Svg
      }
    }
  }
`;
